import classnames from "classnames";
import React, { ReactNode, useEffect, useState } from "react";
import { useClickAway } from "react-use";
import { Wrapper } from "../Grid";
import styles from "./ToolBar.module.scss";
import PlainButton from "../Button/PlainButton/PlainButton";
import MenuIcon from "../../icons/MenuIcon";
import CrossIcon from "../../icons/CrossIcon";
import { useString } from "../../i18n/GetText";

interface ToolBarProps {
  containWidth?: boolean;
  logo?: JSX.Element;
  titleCmp?: ReactNode;
  left?: ReactNode;
  right?: ReactNode;
  middle?: ReactNode;
  className?: string;
  onMobileClose?: () => void;
  setFocusTrapActive: (value: boolean) => void;
}

const ToolBar = React.forwardRef(
  (
    {
      containWidth = true,
      left,
      middle,
      right,
      titleCmp,
      logo,
      onMobileClose,
      className,
      setFocusTrapActive
    }: ToolBarProps,
    ref: React.ForwardedRef<HTMLDivElement>
  ) => {
    const [showMenu, setShowMenu] = useState<boolean>(false);
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const menuRef = React.useRef<HTMLDivElement>(null);
    const Container = containWidth ? Wrapper : "div";
    useClickAway(menuRef, e => {
      if (
        buttonRef.current &&
        e.target instanceof Node &&
        buttonRef.current.contains(e.target)
      ) {
        // Clicks on the button should only be handled by handleClick.
        return;
      }
      setShowMenu(false);
      if (onMobileClose) {
        onMobileClose();
      }
    });
    const handleClick = () => {
      const currentValue = showMenu;
      setShowMenu(!currentValue);
      if (currentValue && onMobileClose) {
        onMobileClose();
      }
    };

    useEffect(() => {
      // block scrolling of body when modal open! <body /> isn't part
      // of React.DOM so we need to use javascript to style it.
      if (right && showMenu) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "inherit";
      }
    }, [right, showMenu]);

    useEffect(() => {
      setFocusTrapActive(showMenu);
    }, [setFocusTrapActive, showMenu]);
    const menuAriaLabel = useString(
      showMenu ? "close-main-menu" : "open-main-menu"
    );
    return (
      <div className={classnames(styles.root, className)} ref={ref}>
        <Container className={styles.container}>
          <div className={styles.logoItem}>
            {logo && React.cloneElement(logo, { className: styles.logo })}
            {titleCmp && titleCmp}
            {left && <div className={styles.left}>{left}</div>}
          </div>
          {middle && <div className={styles.middle}>{middle}</div>}
          {right && (
            <div
              className={classnames(
                styles.rightContainer,
                showMenu ? styles.showMenu : styles.hideMenu
              )}
            >
              <div
                ref={menuRef}
                className={classnames(
                  styles.right,
                  showMenu ? styles.showMenu : styles.hideMenu
                )}
              >
                {right}
              </div>
            </div>
          )}
          {right && (
            <div className={styles.mobileNavContainer}>
              <PlainButton
                ref={buttonRef}
                onClick={handleClick}
                id="main-menu-button"
                aria-haspopup="true"
                aria-controls="main-menu"
                aria-label={menuAriaLabel}
              >
                {showMenu ? (
                  <CrossIcon stroke={"#ffffff"} height={18} width={18} />
                ) : (
                  <MenuIcon stroke={"#ffffff"} height={18} width={18} />
                )}
              </PlainButton>
            </div>
          )}
        </Container>
      </div>
    );
  }
);

export default ToolBar;
